import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/redux/store';
import { questionnairesApi } from 'app/services/questionnaires';
import lodashSet from 'lodash.set';

export type TStHomeSaleList = TStHomeSale[];

export type TStHomeSale = {
  propertyAddress: Nullable<string>;
  propertyCountry: Nullable<string>;
  dateOfSale: Nullable<string>;
  salePricegross: Nullable<string>;
  originalPurchasePrice: Nullable<string>;
  foreignTaxPaidOnCapitalGainsFormPropertySale: Nullable<string>;
  datePropertyPurchased: Nullable<string>;
  improvementsToPropoerty: TImprovementsToPropoerty;
  fixingUpExpensesDoneWithin90DaysOfDateOfSale: TFixingUpExpensesDoneWithin90DaysOfDateOfSale;
  yearInWhichTheTaxWasActuallyPaid: Nullable<string>;
  saleExpenses: TSaleExpenses;
  wasThePropertyJointlyOwnedWithYourSpouse: Nullable<string>;
  hasThePropertyBeenOccupiedByYouAsYourMainResidenceForAtLeastTwoOutOfTheFiveYearsPriorToTheSale: Nullable<string>;
  hasThePropertyBeenRentedForAnyPeriodBetweenDecember312008AndTheSaleDate: Nullable<string>;
  isThePropertyInTheUs: Nullable<string>;
};

export type TFixingUpExpensesDoneWithin90DaysOfDateOfSale =
  TFixingUpExpensesDoneWithin90DaysOfDateOfSaleItem[];

export type TFixingUpExpensesDoneWithin90DaysOfDateOfSaleItem = {
  description: Nullable<string>;
  date: Nullable<string>;
  amount: Nullable<string>;
};

export type TSaleExpenses = TSaleExpensesItem[];

export type TSaleExpensesItem = {
  description: Nullable<string>;
  date: Nullable<string>;
  amount: Nullable<string>;
};

export type TImprovementsToPropoerty = TImprovementsToPropoertyItem[];

export type TImprovementsToPropoertyItem = {
  description: Nullable<string>;
  date: Nullable<string>;
  amount: Nullable<string>;
};
// type FieldArrayKeys =
//   | 'StHomeSaleList'
//   | 'fixingUpExpensesDoneWithin90DaysOfDateOfSale'
//   | 'saleExpenses'
//   | 'improvementsToPropoerty';

export interface HomeSaleStateValues {
  StHomeSaleList: TStHomeSaleList;
  ifNoHasTheSaleTakenPlaceDueToAChangeInYourPlaceOfEmploymentHealthOrUnforeseenCircumstances: Nullable<TYesOrNo>;
  ifYesPleaseExplainTheCircumstances: Nullable<string>;
  pleaseGiveDetailsOfPeriodsRentedOut: Nullable<string>;
  pleaseUploadForm1099S: Nullable<string>;
  endOf1StHomeSaleListRecord: Nullable<string>;
  asYouAreNotFilingJointlyWhoWillBeResponsibleForTheGainOrLoss: Nullable<string>;
}

export interface HomeSaleState {
  values: HomeSaleStateValues;
}

const initialState: HomeSaleState = {
  values: {
    StHomeSaleList: [
      {
        propertyAddress: null,
        propertyCountry: null,
        dateOfSale: null,
        salePricegross: null,
        originalPurchasePrice: null,
        foreignTaxPaidOnCapitalGainsFormPropertySale: null,
        datePropertyPurchased: null,
        yearInWhichTheTaxWasActuallyPaid: null,
        improvementsToPropoerty: [
          { description: null, date: null, amount: null },
        ],
        fixingUpExpensesDoneWithin90DaysOfDateOfSale: [
          { description: null, date: null, amount: null },
        ],
        saleExpenses: [{ description: null, date: null, amount: null }],
        wasThePropertyJointlyOwnedWithYourSpouse: null,
        hasThePropertyBeenOccupiedByYouAsYourMainResidenceForAtLeastTwoOutOfTheFiveYearsPriorToTheSale:
          null,
        hasThePropertyBeenRentedForAnyPeriodBetweenDecember312008AndTheSaleDate:
          null,
        isThePropertyInTheUs: null,
      },
    ],
    ifNoHasTheSaleTakenPlaceDueToAChangeInYourPlaceOfEmploymentHealthOrUnforeseenCircumstances:
      null,
    ifYesPleaseExplainTheCircumstances: null,
    pleaseGiveDetailsOfPeriodsRentedOut: null,
    pleaseUploadForm1099S: null,
    endOf1StHomeSaleListRecord: null,
    asYouAreNotFilingJointlyWhoWillBeResponsibleForTheGainOrLoss: null,
  },
};

const homeSaleSlice = createSlice({
  name: 'homeSale',
  initialState,
  reducers: {
    update(state, action: PayloadAction<HomeSaleStateValues>) {
      state.values = JSON.parse(JSON.stringify(action.payload));
    },
    updateKey(
      state,
      action: PayloadAction<{
        key:
          | keyof HomeSaleStateValues
          | keyof TStHomeSale
          | keyof TFixingUpExpensesDoneWithin90DaysOfDateOfSaleItem
          | keyof TSaleExpensesItem
          | keyof TImprovementsToPropoertyItem;
        value: any;
      }>,
    ) {
      lodashSet(state, `values.${action.payload.key}`, action.payload.value);
    },
    updateFieldArrayKey(
      state,
      action: PayloadAction<{
        key:
          | keyof HomeSaleStateValues
          | keyof TStHomeSale
          | keyof TFixingUpExpensesDoneWithin90DaysOfDateOfSaleItem
          | keyof TSaleExpensesItem
          | keyof TImprovementsToPropoertyItem;
        value: TStHomeSaleList;
      }>,
    ) {
      const fieldArray = action.payload.value.map((field) => ({
        ...field,
        saleExpenses: (field.saleExpenses || []).map((item) => ({ ...item })),

        improvementsToPropoerty: (field.improvementsToPropoerty || []).map(
          (item) => ({
            ...item,
          }),
        ),

        fixingUpExpensesDoneWithin90DaysOfDateOfSale: (
          field.fixingUpExpensesDoneWithin90DaysOfDateOfSale || []
        ).map((item) => ({
          ...item,
        })),
      }));
      if (action.payload.key === 'StHomeSaleList') {
        state.values[action.payload.key] = fieldArray;
      } else {
        lodashSet(state, `values.${action.payload.key}`, fieldArray);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      questionnairesApi.endpoints.getQuestionnaireById.matchFulfilled,
      (state, { payload }) => {
        state.values = payload.questionnaire.data.taxQuestionnaire
          ? payload.questionnaire.data.taxQuestionnaire.income.homeSale.values
          : initialState.values;
      },
    );
  },
});

export const { update, updateKey, updateFieldArrayKey } = homeSaleSlice.actions;
export default homeSaleSlice.reducer;

export const selectHomeSale = (state: RootState) =>
  state.taxQuestionnaire.income.homeSale;
